import { SetStatus } from '@/Enums/enum';
import type { Json } from '@/lib/database.types';
import { supabase } from '@/lib/supabase';
import { removeCommentTags } from '@/lib/utils';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

interface UpdateDraftFragProps {
	userRole: string;
	variables: {
		courseDraft: string;
		courseId: string;
		isDraft?: boolean;
	};
}

export const updateDraftFrag = async (config: UpdateDraftFragProps) => {
	const { variables } = config;
	const { courseId, courseDraft, isDraft } = variables;

	const updateData: any = {
		working_copy: isDraft ? removeCommentTags(courseDraft) : courseDraft,
		is_saved_draft: !!isDraft,
	};

	if (!isDraft) {
		updateData.course_draft = courseDraft;
	}

	const { data, error } = await supabase
		.from('courses')
		.update(updateData)
		.eq('course_id', courseId)
		.select('course_id, submission_id')
		.single();

	if (error) throw error;

	return { update_courses_by_pk: data };
};

export const existingSub = async (config: any) => {
	const { variables } = config;

	const { data, error } = await supabase
		.from('submissions')
		.select('submission_id')
		.eq('course_id', variables.courseId)
		.eq('school_id', schoolId)
		.maybeSingle();

	if (error) throw error;

	return data?.submission_id;
};

export const updateSubStatusFrag = async (config: any) => {
	const { variables } = config;

	// Get submission ID
	const { data: submissionData, error: queryError } = await supabase
		.from('submissions')
		.select('submission_id')
		.eq('course_id', variables.courseId)
		.eq('school_id', schoolId)
		.maybeSingle();

	if (queryError) throw queryError;

	if (!submissionData?.submission_id) {
		throw new Error('Submission not found');
	}

	// Update submission status
	const { error: updateError } = await supabase
		.from('submissions')
		.update({
			admin_approval: SetStatus.WAITING,
			dept_approval: SetStatus.WAITING,
		})
		.eq('submission_id', submissionData.submission_id);

	if (updateError) throw updateError;

	return { affected_rows: 1 };
};

export const setDraftFrag = async (config: any) => {
	const { variables, userRole } = config;
	const { courseId, status, approveAll } = variables;
	const { draftCourse } = config;
	const approvalType = userRole === 'admin' ? 'admin_approval' : 'dept_approval';

	const updateData: any = {};
	if (approveAll) {
		updateData.admin_approval = status;
		updateData.dept_approval = status;
	} else {
		updateData[approvalType] = status;
	}

	const { data: updatedSubmission, error: updateError } = await supabase
		.from('submissions')
		.update(updateData)
		.eq('course_id', courseId)
		.eq('school_id', schoolId)
		.select('admin_approval, dept_approval')
		.single();

	if (updateError) throw updateError;

	const isFullyApproved =
		updatedSubmission.admin_approval === SetStatus.APPROVED &&
		updatedSubmission.dept_approval === SetStatus.APPROVED;

	if (isFullyApproved) {
		const cleanDraft = JSON.parse(removeCommentTags(JSON.stringify(draftCourse)));

		const { error } = await supabase.rpc('publish_course_draft', {
			p_course_id: courseId,
			p_school_id: schoolId,
			p_draft: cleanDraft,
		});

		if (error) throw error;
	}

	return { update_courses_by_pk: { course_id: courseId } };
};

export const updateLessonPlans = async ({
	plans,
	courseId,
}: {
	plans: LessonSection[];
	courseId: string;
}) => {
	const { data, error } = await supabase
		.from('courses')
		.update({ course_lesson_plans: plans as unknown as Json })
		.eq('course_id', courseId)
		.select('course_id')
		.single();

	if (error) throw error;

	return { update_courses_by_pk: data };
};

export const saveWorkingDraft = async (draft: any, courseId: string) => {
	const jsonDraft = JSON.stringify(draft);

	const { data, error } = await supabase
		.from('courses')
		.update({ course_draft: jsonDraft })
		.eq('course_id', courseId)
		.select('course_id')
		.single();

	if (error) throw error;

	return { update_courses_by_pk: data };
};

export interface AddNewCourseOptions {
	disciplineId: string;
	divisionId: string;
	courseName: string;
	grade: string;
	isAp: boolean;
}

export const addNewCourse = async (variables: AddNewCourseOptions) => {
	const { courseName, grade, isAp, disciplineId, divisionId } = variables;

	// Create the course
	const { data: course, error: courseError } = await supabase
		.from('courses')
		.insert({
			course_name: courseName,
			grade,
			is_ap: isAp,
			school_id: schoolId,
		})
		.select('course_id')
		.single();

	if (courseError) throw courseError;

	if (!course?.course_id) {
		throw new Error('Failed to create course');
	}

	// Link course to discipline
	if (disciplineId) {
		const { error: disciplineError } = await supabase.from('courses_disciplines').insert({
			course_id: course.course_id,
			discipline_id: disciplineId,
		});

		if (disciplineError) throw disciplineError;
	}

	// Link course to division
	if (divisionId) {
		const { error: divisionError } = await supabase.from('courses_divisions').insert({
			course_id: course.course_id,
			division_id: divisionId,
		});

		if (divisionError) throw divisionError;
	}

	return { insert_courses_one: course };
};
